import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/app/routes'
import { Button } from '@/ui/button'
import { MaterialIcon } from '@/ui/material-icon'
import styles from './AdpLandingPage.module.scss'

const ADP_PIPELINE_ROUTE = ROUTES.AKASHIC_ADP_PIPELINE
const AUD_UPLOAD_ROUTE = ROUTES.AKASHIC_AUD_UPLOAD

type ModuleCard = {
  title: string
  description: string
  icon: string
  accent: 'orange' | 'purple' | 'teal'
  /** When set, Open / Learn More / card click navigate here (e.g. ADP pipeline builder). */
  route?: string
}

type ModuleGroup = {
  id: string
  title: string
  count?: string
  icon: string
  cards?: ModuleCard[]
}

const GROUPS: ModuleGroup[] = [
  {
    id: 'data-engineering',
    title: 'Data Engineering',
    count: '3 modules',
    icon: 'developer_board',
    cards: [
      {
        title: 'Akashic Data Pipeline (ADP)',
        description:
          'Streamline data movement with Extract, Transform, and Load (ETL) processes. Ensure clean, efficient, and scalable data handling.',
        icon: 'timeline',
        accent: 'orange',
        route: ADP_PIPELINE_ROUTE,
      },
      {
        title: 'Akashic Data Warehouse (ADW)',
        description:
          'The Akashic Data Warehouse (ADW) is a powerful, scalable solution designed to store.',
        icon: 'inventory_2',
        accent: 'purple',
      },
      {
        title: 'Akashic Unstructured Data Processing (AUDP)',
        description:
          'Upload medical documents, review sufficiency criteria, and submit cases for QAR approval.',
        icon: 'description',
        accent: 'teal',
        route: AUD_UPLOAD_ROUTE,
      },
    ],
  },
  {
    id: 'ai-analytics',
    title: 'AI, Analytics & Insights',
    count: '3 modules',
    icon: 'analytics',
  },
  {
    id: 'data-governance',
    title: 'Data Management & Governance',
    count: '2 modules',
    icon: 'policy',
  },
  {
    id: 'workflows',
    title: 'Workflows & Automation',
    icon: 'call_split',
  },
]

export default function AkashicLandingPage() {
  const navigate = useNavigate()
  const [openGroups, setOpenGroups] = useState<string[]>(['data-engineering'])
  const allExpanded = openGroups.length === GROUPS.length

  const toggleGroup = (id: string) => {
    setOpenGroups(current =>
      current.includes(id) ? current.filter(entry => entry !== id) : [...current, id]
    )
  }

  const toggleAll = () => {
    setOpenGroups(allExpanded ? [] : GROUPS.map(group => group.id))
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.brandSection}>
          <span className={styles.brandMark} aria-hidden>
            <MaterialIcon name="hub" size={18} />
          </span>
          <span className={styles.brandLogo}>AKASHIC</span>
        </div>
        <div className={styles.headerActions}>
          <Button className={styles.adminButton}>
            <MaterialIcon name="room_preferences" size={20} />
            Admin Center
          </Button>
          <div className={styles.notifWrap}>
            <Button type="button" variant="ghost" size="icon" aria-label="Notifications">
              <MaterialIcon name="notifications" size={22} />
            </Button>
            <span className={styles.badge}>3</span>
          </div>
          <img className={styles.avatar} src="https://i.pravatar.cc/56?img=12" alt="User avatar" />
        </div>
      </header>

      <main className={styles.main}>
        <section className={styles.heroRow}>
          <div className={styles.welcomeCard}>
            <div className={styles.welcomeText}>
              <h1>Welcome Sarath!</h1>
              <p>
                Access your modular analytics suite. Choose a module to get started or continue your
                work.
              </p>
            </div>
            <div className={styles.welcomeBadge}>DHIRA&apos;S Akashic</div>
          </div>
          <div className={styles.expandBar}>
            <Button type="button" variant="outline" onClick={toggleAll} className={styles.expandBarBtn}>
              <MaterialIcon name="unfold_more" size={18} />
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </div>
        </section>

        <section className={styles.groups}>
          {GROUPS.map(group => {
            const isOpen = openGroups.includes(group.id)
            return (
              <article
                key={group.id}
                className={`${styles.groupCard} ${isOpen ? styles.groupCardOpen : ''}`}
              >
                <button
                  type="button"
                  className={styles.groupHeader}
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={isOpen}
                >
                  <div className={styles.groupInfo}>
                    <span className={styles.groupIconWrap}>
                      <MaterialIcon name={group.icon} size={22} />
                    </span>
                    <div className={styles.groupTitles}>
                      <h3>{group.title}</h3>
                      {group.count ? <p>{group.count}</p> : null}
                    </div>
                  </div>
                  <MaterialIcon
                    name={isOpen ? 'expand_less' : 'expand_more'}
                    size={22}
                    className={styles.groupChevron}
                  />
                </button>

                {isOpen && group.cards && (
                  <div className={styles.moduleGrid}>
                    {group.cards.map(card => (
                      <div
                        key={card.title}
                        className={`${styles.moduleCard} ${styles[`accent${card.accent}`]} ${card.route ? styles.moduleCardClickable : ''}`}
                        onClick={
                          card.route
                            ? () => {
                              navigate(card.route!)
                            }
                            : undefined
                        }
                      >
                        <div className={styles.moduleIconColumn}>
                          <div className={styles.moduleIconPanel}>
                            <MaterialIcon name={card.icon} size={40} />
                          </div>
                        </div>
                        <div className={styles.moduleContent}>
                          <h4>{card.title}</h4>
                          <p>{card.description}</p>
                          <div className={styles.moduleActions}>
                            <Button
                              type="button"
                              variant="ghost"
                              className={styles.linkBtn}
                              onClick={e => {
                                e.stopPropagation()
                                if (card.route) navigate(card.route)
                              }}
                            >
                              Learn More
                              <MaterialIcon name="arrow_forward" size={16} />
                            </Button>
                            <Button
                              variant="outline"
                              className={styles.openBtn}
                              type="button"
                              onClick={e => {
                                e.stopPropagation()
                                if (card.route) navigate(card.route)
                              }}
                            >
                              Open
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            )
          })}
        </section>
      </main>
    </div>
  )
}
